import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Text, Badge, Button, HStack, VStack, Flex, Divider, useToast,
  Table, Thead, Tbody, Tr, Th, Td, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Input, Textarea,
  Select, useDisclosure, IconButton, Switch, FormControl, FormLabel, SimpleGrid,
} from '@chakra-ui/react';
import { FiRefreshCw, FiEdit2, FiX, FiPlus } from 'react-icons/fi';
import api from '../api';

const INTERVAL_LABELS = {
  weekly:'Weekly', monthly:'Monthly', quarterly:'Quarterly', biannual:'Every 6 months', yearly:'Yearly'
};
const STATUS_COLOR = {
  future_contractual:'purple', to_invoice:'blue', invoiced_within_terms:'teal',
  paid_in_full:'green', paid_in_part:'yellow', overdue:'orange', very_overdue:'red',
  cancelled:'gray',
};
const fmt = d => d ? new Date(d.split('T')[0]+'T12:00:00').toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const fmtMoney = v => `£${parseFloat(v||0).toFixed(2)}`;

function ChainModal({ sub, isOpen, onClose }) {
  const [invoices, setInvoices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [cancelId, setCancelId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const toast = useToast();

  useEffect(() => {
    if (isOpen && sub) api.get(`/subscriptions/${sub.id}/invoices`).then(r=>setInvoices(r.data||[])).catch(()=>{});
  }, [isOpen, sub]);

  const saveEdit = async (id) => {
    try {
      await api.put(`/subscriptions/invoice/${id}`, { issue_date: editDate });
      setEditing(null);
      const {data} = await api.get(`/subscriptions/${sub.id}/invoices`);
      setInvoices(data);
      toast({title:'Updated',status:'success',duration:1500});
    } catch(e) { toast({title:'Failed',status:'error',duration:2000}); }
  };

  const cancelOne = async (id) => {
    try {
      await api.put(`/subscriptions/invoice/${id}`, { cancel: true, cancel_reason: cancelReason });
      setCancelId(null); setCancelReason('');
      const {data} = await api.get(`/subscriptions/${sub.id}/invoices`);
      setInvoices(data);
      toast({title:'Invoice cancelled',status:'info',duration:1500});
    } catch(e) { toast({title:'Failed',status:'error',duration:2000}); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent>
        <ModalHeader fontSize="md">{sub?.project_name} — Invoice Chain</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Table size="sm">
            <Thead><Tr>
              <Th>#</Th><Th>Date</Th><Th>Status</Th><Th>Total</Th><Th>Actions</Th>
            </Tr></Thead>
            <Tbody>
              {invoices.map(inv => (
                <Tr key={inv.id} opacity={inv.deleted_at ? 0.5 : 1}
                  bg={inv.status === 'future_contractual' ? 'purple.50' : 'white'}>
                  <Td fontSize="11px" color="gray.500">#{inv.sequence_number || 1}</Td>
                  <Td fontSize="11px">
                    {editing === inv.id ? (
                      <HStack spacing={1}>
                        <Input size="xs" type="date" value={editDate} onChange={e=>setEditDate(e.target.value)} w="130px"/>
                        <Button size="xs" colorScheme="teal" onClick={()=>saveEdit(inv.id)}>✓</Button>
                        <Button size="xs" variant="ghost" onClick={()=>setEditing(null)}>✗</Button>
                      </HStack>
                    ) : fmt(inv.issue_date)}
                  </Td>
                  <Td><Badge colorScheme={STATUS_COLOR[inv.status]||'gray'} fontSize="8px">{(inv.status||'').replace(/_/g,' ')}</Badge></Td>
                  <Td fontSize="11px">{fmtMoney(inv.total_gross)}</Td>
                  <Td>
                    {!inv.deleted_at && inv.status === 'future_contractual' && (
                      <HStack spacing={1}>
                        <IconButton size="xs" icon={<FiEdit2 size={10}/>} variant="ghost"
                          onClick={()=>{ setEditing(inv.id); setEditDate(inv.issue_date?.split('T')[0]||''); }}
                          aria-label="Edit date"/>
                        {cancelId === inv.id ? (
                          <HStack>
                            <Input size="xs" placeholder="Reason..." value={cancelReason} onChange={e=>setCancelReason(e.target.value)} w="120px"/>
                            <Button size="xs" colorScheme="red" onClick={()=>cancelOne(inv.id)}>Cancel invoice</Button>
                            <Button size="xs" variant="ghost" onClick={()=>setCancelId(null)}>✗</Button>
                          </HStack>
                        ) : (
                          <Button size="xs" variant="ghost" colorScheme="red" onClick={()=>setCancelId(inv.id)}>Cancel</Button>
                        )}
                      </HStack>
                    )}
                    {inv.deleted_at && <Badge colorScheme="red" fontSize="8px">Cancelled</Badge>}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </ModalBody>
        <ModalFooter><Button size="sm" variant="ghost" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function ExtendModal({ sub, isOpen, onClose, onDone }) {
  const [periods, setPeriods] = useState(1);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/subscriptions/${sub.id}/extend`, { extra_periods: periods });
      toast({title:`Extended by ${periods} period(s)`,status:'success',duration:2000});
      onDone(); onClose();
    } catch(e) { toast({title:'Failed',status:'error',duration:2000}); }
    setSaving(false);
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" isCentered>
      <ModalOverlay /><ModalContent>
        <ModalHeader fontSize="md">Extend Contractual Term</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="12px" color="gray.600" mb={3}>
            Add additional periods to <strong>{sub?.project_name}</strong> ({INTERVAL_LABELS[sub?.repeat_interval] || sub?.repeat_interval})
          </Text>
          <FormControl><FormLabel fontSize="xs">Extra Periods to Add</FormLabel>
            <Input type="number" min={1} max={24} value={periods} onChange={e=>setPeriods(parseInt(e.target.value)||1)} size="sm"/>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="teal" onClick={save} isLoading={saving}>Extend</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function CancelSubModal({ sub, isOpen, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const save = async () => {
    if (!reason) { toast({title:'Please provide a reason',status:'warning',duration:2000}); return; }
    setSaving(true);
    try {
      await api.post(`/subscriptions/${sub.id}/cancel`, { reason, cancel_future_from: fromDate });
      toast({title:'Subscription cancelled',status:'info',duration:2000});
      onDone(); onClose();
    } catch(e) { toast({title:'Failed',status:'error',duration:2000}); }
    setSaving(false);
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay /><ModalContent>
        <ModalHeader fontSize="md">Cancel Subscription</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3}>
            <Text fontSize="12px" color="gray.600" w="100%">
              This will mark the subscription as cancelled and delete all future contractual invoices from the chosen date onward.
            </Text>
            <FormControl isRequired><FormLabel fontSize="xs">Cancellation Reason</FormLabel>
              <Textarea size="sm" rows={2} value={reason} onChange={e=>setReason(e.target.value)} placeholder="Required"/>
            </FormControl>
            <FormControl><FormLabel fontSize="xs">Cancel future invoices from date</FormLabel>
              <Input size="sm" type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)}/>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Close</Button>
          <Button size="sm" colorScheme="red" onClick={save} isLoading={saving}>Cancel Subscription</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function SubscriptionsDashboard({ isOpen, onClose, onBack, fullPage }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const chainDisc = useDisclosure();
  const extendDisc = useDisclosure();
  const cancelDisc = useDisclosure();

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const {data} = await api.get('/subscriptions'); setSubs(data||[]); } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { if (isOpen) fetch(); }, [isOpen, fetch]);

  const activeSubs = subs.filter(s => !s.subscription_cancelled_at);
  const cancelledSubs = subs.filter(s => s.subscription_cancelled_at);
  const totalContracted = subs.reduce((acc, s) => acc + parseFloat(s.total_gross||0) * (s.pending_count||0), 0);

  const SubCard = ({s}) => (
    <Box p={3} border="1px" borderColor={s.subscription_cancelled_at?'gray.200':'teal.200'} borderRadius="md"
      bg={s.subscription_cancelled_at?'gray.50':'white'} opacity={s.subscription_cancelled_at?0.7:1}>
      <Flex justify="space-between" align="flex-start">
        <Box flex={1}>
          <HStack spacing={2} mb={1}>
            <Badge colorScheme={s.billing_type==='subscription'?'teal':'blue'} fontSize="8px">
              {s.billing_type==='subscription'?'♾️ Subscription':'🔁 Repeat'}
            </Badge>
            <Badge colorScheme="gray" fontSize="8px">{INTERVAL_LABELS[s.repeat_interval]||s.repeat_interval}</Badge>
            {s.subscription_cancelled_at && <Badge colorScheme="red" fontSize="8px">Cancelled</Badge>}
            {s.subscription_rolling && !s.subscription_cancelled_at && <Badge colorScheme="purple" fontSize="8px">Rolling</Badge>}
          </HStack>
          <Text fontWeight="700" fontSize="13px">{s.project_name}</Text>
          {s.company_name && <Text fontSize="11px" color="gray.500">{s.company_name}</Text>}
          <HStack spacing={3} mt={1} fontSize="10px" color="gray.500">
            <Text>Started: {fmt(s.issue_date)}</Text>
            {s.subscription_end_date && <Text>Ends: {fmt(s.subscription_end_date)}</Text>}
            <Text>Per invoice: {fmtMoney(s.total_gross)}</Text>
            <Text>Future: <strong>{s.pending_count||0}</strong> invoices</Text>
          </HStack>
          {s.subscription_cancelled_at && (
            <Text fontSize="10px" color="red.500" mt={1}>
              Cancelled {fmt(s.subscription_cancelled_at)}: {s.subscription_cancel_reason}
            </Text>
          )}
        </Box>
        <HStack spacing={1} ml={2}>
          <Button size="xs" variant="outline" colorScheme="purple"
            onClick={()=>{ setSelected(s); chainDisc.onOpen(); }}>
            View Chain ({(s.future_count||0)+1})
          </Button>
          {!s.subscription_cancelled_at && <>
            <Button size="xs" variant="outline" colorScheme="teal"
              onClick={()=>{ setSelected(s); extendDisc.onOpen(); }}>
              Extend
            </Button>
            <Button size="xs" variant="outline" colorScheme="red"
              onClick={()=>{ setSelected(s); cancelDisc.onOpen(); }}>
              Cancel
            </Button>
          </>}
        </HStack>
      </Flex>
    </Box>
  );

  if (!isOpen && !fullPage) return null;

  return (
    <>
    <Box h="100%" overflowY="auto" p={4}>
      <Flex justify="space-between" align="center" mb={4}>
        <HStack>
          {onBack && <Button size="sm" variant="ghost" onClick={onBack}>← Back</Button>}
          <Text fontSize="lg" fontWeight="700">Subscriptions</Text>
        </HStack>
        <HStack>
          <IconButton icon={<FiRefreshCw size={13}/>} size="sm" variant="ghost" onClick={fetch} aria-label="Refresh"/>
        </HStack>
      </Flex>

      {/* Summary */}
      <SimpleGrid columns={3} spacing={3} mb={4}>
        <Box bg="teal.50" p={3} borderRadius="md" textAlign="center">
          <Text fontSize="22px" fontWeight="800" color="teal.600">{activeSubs.length}</Text>
          <Text fontSize="10px" color="gray.500">Active Subscriptions</Text>
        </Box>
        <Box bg="purple.50" p={3} borderRadius="md" textAlign="center">
          <Text fontSize="22px" fontWeight="800" color="purple.600">
            {subs.reduce((a,s)=>a+(s.pending_count||0),0)}
          </Text>
          <Text fontSize="10px" color="gray.500">Future Contractual Invoices</Text>
        </Box>
        <Box bg="green.50" p={3} borderRadius="md" textAlign="center">
          <Text fontSize="22px" fontWeight="800" color="green.600">{fmtMoney(totalContracted)}</Text>
          <Text fontSize="10px" color="gray.500">Contracted Future Value</Text>
        </Box>
      </SimpleGrid>

      {loading ? <Text color="gray.400" textAlign="center" py={8}>Loading...</Text> : (
        <VStack spacing={2} align="stretch">
          {activeSubs.length === 0 && cancelledSubs.length === 0 && (
            <Text color="gray.400" textAlign="center" py={8}>No subscriptions or repeat billing invoices yet.</Text>
          )}
          {activeSubs.map(s => <SubCard key={s.id} s={s}/>)}
          {cancelledSubs.length > 0 && (
            <>
              <Divider my={2}/>
              <Text fontSize="11px" color="gray.400" fontWeight="600" textTransform="uppercase">Cancelled</Text>
              {cancelledSubs.map(s => <SubCard key={s.id} s={s}/>)}
            </>
          )}
        </VStack>
      )}
    </Box>

    <ChainModal sub={selected} isOpen={chainDisc.isOpen} onClose={chainDisc.onClose}/>
    <ExtendModal sub={selected} isOpen={extendDisc.isOpen} onClose={extendDisc.onClose} onDone={fetch}/>
    <CancelSubModal sub={selected} isOpen={cancelDisc.isOpen} onClose={cancelDisc.onClose} onDone={fetch}/>
    </>
  );
}
